import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../home/components/Footer';

const stats = [
  { value: '5,000+', label: 'Businesses Served' },
  { value: '10M+', label: 'Calls Handled' },
  { value: '99.9%', label: 'Uptime Guarantee' },
  { value: '24/7', label: 'Always Available' },
];

const values = [
  {
    icon: 'ri-lightbulb-line',
    title: 'Innovation First',
    description: 'We push the boundaries of AI technology to deliver solutions that feel genuinely human and intuitive.',
    bg: 'bg-amber-50',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Trust & Security',
    description: 'Your data is sacred. We employ enterprise‑grade encryption and strict privacy protocols at every level.',
    bg: 'bg-emerald-50',
  },
  {
    icon: 'ri-heart-pulse-line',
    title: 'Customer Obsession',
    description: 'Every feature we build starts with a real customer need. Your success is the only metric that matters.',
    bg: 'bg-rose-50',
  },
  {
    icon: 'ri-team-line',
    title: 'Collaboration',
    description: 'We believe the best products are built by diverse teams working together toward a shared vision.',
    bg: 'bg-sky-50',
  },
];

const team = [
  {
    name: 'Gabriel Gabriel',
    role: 'CEO & Co‑Founder',
    bio: 'Former VP of Engineering at a Fortune 500 telecom company with 15+ years in AI and communications.',
    avatar:
      'https://public.readdy.ai/ai/img_res/166183606efb05c8bf0de03be8f3666e.jpg',
  },
];

const milestones = [
  {
    year: '2020',
    title: 'The Spark',
    description:
      'Founded in a small garage with a big idea — what if no business ever had to miss a call again?',
  },
  {
    year: '2021',
    title: 'First 100 Customers',
    description:
      'Launched our beta and onboarded 100 businesses within the first three months. The feedback was electric.',
  },
  {
    year: '2022',
    title: 'Series A Funding',
    description:
      'Raised $12M to expand our AI capabilities, add multi‑language support, and grow the team to 50+.',
  },
  {
    year: '2023',
    title: 'Enterprise Launch',
    description:
      'Rolled out our Enterprise tier, serving organizations with thousands of daily calls across 30+ languages.',
  },
  {
    year: '2024',
    title: '5,000+ Businesses',
    description:
      'Crossed the 5,000 customer milestone and handled over 10 million calls. The journey is just beginning.',
  },
];

export default function AboutPage() {
  const [scrolled, setScrolled] = useState(false);

  // Track scroll position to toggle navbar style
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Ensure the page starts at the top when navigated to
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-md' : 'bg-white shadow-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center cursor-pointer">
            <img
              src="https://static.readdy.ai/image/e31f2f4702878072217cd119276d8644/5b56961b60c36d4ff2ff86c82d13a1e4.png"
              alt="AI Receptionist Logo"
              className="h-12 w-auto"
            />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className="text-sm font-medium text-gray-700 hover:text-purple-600 transition-colors cursor-pointer"
            >
              Home
            </Link>
            <Link
              to="/demo"
              className="text-sm font-medium text-gray-700 hover:text-purple-600 transition-colors cursor-pointer"
            >
              Demo
            </Link>
            <Link
              to="/contact"
              className="text-sm font-medium text-gray-700 hover:text-purple-600 transition-colors cursor-pointer"
            >
              Contact
            </Link>
            <Link
              to="/blog"
              className="text-sm font-medium text-gray-700 hover:text-purple-600 transition-colors cursor-pointer"
            >
              Blog
            </Link>
            <Link
              to="/start-free-trial"
              className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-medium rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
            >
              Get Started
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20modern%20geometric%20shapes%20with%20warm%20coral%20and%20soft%20peach%20gradient%20tones%2C%20minimalist%20artistic%20background%2C%20smooth%20flowing%20curves%2C%20elegant%20and%20professional%20atmosphere%2C%20subtle%20texture%20overlay&width=1920&height=800&seq=about-hero-bg-001&orientation=landscape"
            alt="About Background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/60 to-white"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <p className="text-xs font-bold tracking-widest text-purple-600 uppercase mb-4">
            OUR MISSION
          </p>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight mb-6 max-w-3xl">
            Making Every Business<br />Sound Professional, 24/7
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl leading-relaxed">
            We started AI Receptionist with a simple belief: no business should ever lose a
            customer because of a missed call. Our AI‑powered platform ensures every caller gets a
            warm, professional response — any time, any language, any day.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-purple-50/60">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, i) => (
            <div key={i} className="text-center">
              <p className="text-4xl md:text-5xl font-bold text-gray-900 mb-2">
                {stat.value}
              </p>
              <p className="text-sm text-gray-500 uppercase tracking-wider font-medium">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-xs font-bold tracking-widest text-purple-600 uppercase mb-4">
              WHAT DRIVES US
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              Our Core Values
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((v, i) => (
              <div
                key={i}
                className={`${v.bg} rounded-2xl p-8 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer`}
              >
                <div className="w-14 h-14 flex items-center justify-center mb-5">
                  <i className={`${v.icon} text-4xl text-gray-800`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{v.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{v.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-xs font-bold tracking-widest text-purple-600 uppercase mb-4">
              MEET THE TEAM
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              The People Behind the Product
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              A passionate group of engineers, designers, and business minds united by one goal —
              helping businesses communicate better.
            </p>
          </div>
          <div className="flex justify-center">
            {team.map((member, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer w-full max-w-xs"
              >
                <div className="w-full h-72 overflow-hidden">
                  <img
                    src={member.avatar}
                    alt={member.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900">{member.name}</h3>
                  <p className="text-sm text-purple-600 font-medium mb-2">{member.role}</p>
                  <p className="text-sm text-gray-500 leading-relaxed">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-xs font-bold tracking-widest text-purple-600 uppercase mb-4">
              OUR JOURNEY
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              From Garage to Global
            </h2>
          </div>
          <div className="relative">
            <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-0.5 bg-purple-200 transform md:-translate-x-1/2"></div>
            {milestones.map((m, i) => (
              <div
                key={i}
                className={`relative flex items-start mb-12 last:mb-0 ${
                  i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                <div
                  className={`hidden md:block w-1/2 ${
                    i % 2 === 0 ? 'pr-12 text-right' : 'pl-12 text-left'
                  }`}
                >
                  <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-shadow">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{m.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{m.description}</p>
                  </div>
                </div>

                <div className="absolute left-6 md:left-1/2 transform -translate-x-1/2 w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center z-10 shadow-lg">
                  <span className="text-white text-xs font-bold">{m.year}</span>
                </div>

                <div className={`ml-20 md:ml-0 md:hidden`}>
                  <div className="bg-white rounded-2xl p-6 shadow-md">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{m.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{m.description}</p>
                  </div>
                </div>

                <div className={`hidden md:block w-1/2 ${i % 2 === 0 ? 'pl-12' : 'pr-12'}`}></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-pink-600 to-purple-700">
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage:
                'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '40px 40px',
            }}
          ></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Join 5,000+ Businesses?
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Start your 7‑day free trial today and see why thousands of businesses trust AI
            Receptionist.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/start-free-trial"
              className="px-8 py-4 bg-white text-purple-700 font-semibold rounded-full hover:shadow-2xl transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              Start Free Trial
              <i className="ri-arrow-right-line text-xl"></i>
            </Link>
            <Link
              to="/contact"
              className="px-8 py-4 border-2 border-white text-white font-semibold rounded-full hover:bg-white hover:text-purple-600 transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
